import React, { useState, useEffect, useRef, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useDispatch } from 'react-redux'
import { Stack, Typography, CircularProgress } from '@mui/material'
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material'
import { useTheme } from '@mui/styles'
import { useGetOrgImageQuery, useUploadOrgImageMutation } from 'services/assets'
import DialogModal from 'components/generic/modals/dialog-modal'
import { dispatchSnackbar } from 'utils/helpers'

const CaptureSignatureModal = ({ onSave, onClose, open, signatureImage, ...restProps }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const imageStyle = {
    color: theme.palette.text.hint,
    border: 1,
    borderStyle: 'solid',
    borderColor: theme.palette.text.disabled,
  }

  const { data: orgImage } = useGetOrgImageQuery(
    {
      filename: signatureImage?.filename,
      isTemp: false,
    },
    { skip: !signatureImage?.filename },
  )

  const [uploadOrgImage, { isLoading: isUploadingOrgImage }] = useUploadOrgImageMutation()

  const [src, setSrc] = useState('')
  const [isViewMode, setIsViewMode] = useState(false)
  const [saveDisabled, setSaveDisabled] = useState(true)
  const canvasRef = useRef(null)
  const isDrawing = useRef(false)
  const mousePosition = useRef({ x: null, y: null })
  const signatureInterval = useRef(null)
  const penInterval = useRef(null)

  const startDrawingCallback = useCallback(() => {
    setSaveDisabled(false)
    isDrawing.current = true
  }, [])

  const stopDrawingCallback = useCallback(() => {
    mousePosition.current = { x: null, y: null }
    isDrawing.current = false
  }, [])

  const drawLineCallback = useCallback(event => {
    if (isDrawing.current) {
      const bounding = canvasRef.current?.getBoundingClientRect()
      const newPositionX = event.clientX || event.touches?.[0]?.clientX
      const newPositionY = event.clientY || event.touches?.[0]?.clientY
      const newPosition = {
        x: newPositionX - bounding.left,
        y: newPositionY - bounding.top,
      }
      if (mousePosition.current.x && mousePosition.current.y) {
        const canvasContext = canvasRef.current?.getContext('2d')
        canvasContext.lineWidth = 2
        canvasContext.beginPath()
        canvasContext.moveTo(mousePosition.current.x, mousePosition.current.y)
        canvasContext.lineTo(newPosition.x, newPosition.y)
        canvasContext.closePath()
        canvasContext.stroke()
      }
      mousePosition.current = newPosition
    }
  }, [])

  const preventScrollWhileDrawing = useCallback(event => {
    if (event.target === canvasRef.current) {
      event.preventDefault()
    }
  })

  const startListeningForSignature = async () => {
    const canvasContext = canvasRef.current?.getContext('2d')
    if (canvasContext) {
      canvasContext.fillStyle = '#FFFFFF'
      canvasContext.fillRect(0, 0, canvasRef.current?.width, canvasRef.current?.height)
      canvasRef.current?.addEventListener('mousedown', startDrawingCallback)
      canvasRef.current?.addEventListener('touchstart', startDrawingCallback)
      document?.addEventListener('mouseup', stopDrawingCallback)
      document?.addEventListener('touchend', stopDrawingCallback)
      canvasRef.current?.addEventListener('mousemove', drawLineCallback)
      canvasRef.current?.addEventListener('touchmove', drawLineCallback)

      if (window.IsSigWebInstalled()) {
        // Setup signature pad configuration
        window.SetTabletState(0, signatureInterval.current)
        window.ClearTablet()
        window.SetDisplayXSize(500)
        window.SetDisplayYSize(100)
        window.SetJustifyMode(0)

        // Activate signature pad
        signatureInterval.current = window.SetTabletState(1, canvasContext, 50)
      }
    }
  }

  const cleanup = () => {
    if (window.IsSigWebInstalled()) {
      window.Reset()
    }
    clearInterval(penInterval.current)
    signatureInterval.current = null
    penInterval.current = null

    canvasRef.current?.removeEventListener('mousedown', startDrawingCallback)
    canvasRef.current?.removeEventListener('touchstart', startDrawingCallback)
    document?.removeEventListener('mouseup', stopDrawingCallback)
    document?.removeEventListener('touchend', stopDrawingCallback)
    canvasRef.current?.removeEventListener('mousemove', drawLineCallback)
    canvasRef.current?.removeEventListener('touchmove', drawLineCallback)
    document?.removeEventListener('touchmove', preventScrollWhileDrawing)
  }

  const onCloseWindow = () => {
    cleanup()
    onClose()
  }

  const onSubmit = async () => {
    if (window.IsSigWebInstalled()) {
      window.SetTabletState(0, signatureInterval.current)
    }

    await canvasRef.current?.toBlob(
      async blob => {
        const signatureFormData = new FormData()
        signatureFormData.append('image', blob)

        await uploadOrgImage({
          body: signatureFormData,
        })
          .unwrap()
          .then(response => {
            onSave(response.imageName)
            onCloseWindow()
            dispatchSnackbar(dispatch, 'success', 'Signature uploaded successfully.')
          })
          .catch(error => {
            const errorMessage = error?.data?.error || 'There was an error uploading the signature'
            dispatchSnackbar(dispatch, 'error', errorMessage)
          })
      },
      'image/jpeg',
      1,
    )
  }

  const onRetake = () => {
    setSaveDisabled(true)
    if (isViewMode) {
      setSrc('')
      setIsViewMode(false)
      setTimeout(startListeningForSignature, 500)
    } else {
      if (window.IsSigWebInstalled()) {
        window.ClearTablet()
      }
      const canvasContext = canvasRef.current?.getContext('2d')
      canvasContext.fillStyle = '#FFFFFF'
      canvasContext.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height)
    }
  }

  const renderMessage = () => {
    if (isUploadingOrgImage) {
      return <CircularProgress size="24px" />
    }
    if (src) {
      return (
        <>
          <CheckCircleIcon color="primary" />
          <Typography>Signature captured</Typography>
        </>
      )
    }
    return <Typography>Have customer enter signature now...</Typography>
  }

  useEffect(() => {
    if (orgImage) {
      setSrc(orgImage)
      setIsViewMode(true)
    }
  }, [orgImage])

  useEffect(() => {
    if (open === true && !signatureImage?.filename) {
      setTimeout(startListeningForSignature, 500)
    }
  }, [open])

  // set-up page
  useEffect(() => {
    window.onbeforeunload = event => {
      cleanup()
      event.preventDefault()
    }
    if (window.IsSigWebInstalled()) {
      penInterval.current = setInterval(window.SigWebEvent, 20)
      window.onSigPenDown = () => setSaveDisabled(false)
    }
    document.addEventListener('touchmove', preventScrollWhileDrawing, { passive: false })
  }, [])

  return (
    <DialogModal
      {...restProps}
      open={open}
      onClose={() => {
        if (isUploadingOrgImage) return
        onCloseWindow()
      }}
      onSave={isViewMode ? onCloseWindow : onSubmit}
      extraAction={{
        text: isViewMode ? 'Retry' : 'Clear',
        onClick: onRetake,
        disabled: !isViewMode && saveDisabled,
      }}
      saveDisabled={!isViewMode && saveDisabled}
      buttonTitle="Done"
    >
      <Stack alignItems="center">
        {src ? (
          <img
            src={src}
            alt="signature"
            style={{
              ...imageStyle,
              height: 100,
              width: 400,
            }}
          />
        ) : (
          <canvas ref={canvasRef} id="canvas" name="canvas" width="400" height="100" style={imageStyle} />
        )}
        <Stack flexDirection="row" mt={2}>
          {renderMessage()}
        </Stack>
      </Stack>
    </DialogModal>
  )
}

CaptureSignatureModal.propTypes = {
  ...DialogModal.propTypes,
  onSave: PropTypes.func,
  open: PropTypes.bool,
  signatureImage: PropTypes.object,
}

CaptureSignatureModal.defaultProps = {
  ...DialogModal.defaultProps,
  onSave: () => {},
  open: true,
  signatureImage: {},
}

export default CaptureSignatureModal
